/* ==========================================================================
   Rook UI Core — Popover Controller
   Web Component: <rk-popover-element>
   Hydration tier: 'interaction' (internal trigger)
   ========================================================================== */

import { RkComponent } from '@rook/component';

function waitForExitAnimation(el, done) {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!el || reduce) {
        done();
        return () => {};
    }

    let settled = false;
    const onEnd = (e) => {
        if (e.target !== el) return;
        finish();
    };
    const finish = () => {
        if (settled) return;
        settled = true;
        el.removeEventListener('transitionend', onEnd);
        clearTimeout(timer);
        done();
    };
    const cancel = () => {
        if (settled) return;
        settled = true;
        el.removeEventListener('transitionend', onEnd);
        clearTimeout(timer);
    };
    el.addEventListener('transitionend', onEnd);
    const timer = setTimeout(finish, 500);
    return cancel;
}

class RkPopoverElement extends RkComponent {
    static hydration = 'interaction';

    #cancelExitAnim = null;
    #onClickOutside = null;
    #onKeyDown = null;
    #onResize = null;

    attachHydrationTrigger(hydrate) {
        const onClick = (event) => {
            const trigger = event.target.closest('[data-rk-popover-trigger]');
            if (!trigger || !this.contains(trigger)) return;
            this.removeEventListener('click', onClick, true);
            event.preventDefault();
            event.stopPropagation();
            hydrate();
            this.toggle();
        };
        this.addEventListener('click', onClick, true);
        return () => this.removeEventListener('click', onClick, true);
    }

    setup() {
        this.trigger = this.querySelector('[data-rk-popover-trigger]');
        this.content = this.querySelector('.rk-popover__content');
        this.previousFocus = null;

        if (!this.trigger || !this.content) return;

        this.delegate({
            'click [data-rk-popover-trigger]': this.onTriggerClick,
        });

        this.trigger.setAttribute('aria-haspopup', 'dialog');
        this.trigger.setAttribute('aria-expanded', 'false');

        const contentId = this.content.id || `rk-popover-${this.#uid()}`;
        this.content.id = contentId;
        this.trigger.setAttribute('aria-controls', contentId);
    }

    teardown() {
        this.#removeGlobalListeners();
        if (this.#cancelExitAnim) {
            this.#cancelExitAnim();
            this.#cancelExitAnim = null;
        }
    }

    onTriggerClick(event) {
        event.preventDefault();
        event.stopPropagation();
        this.toggle();
    }

    toggle() {
        const closing = this.content?.dataset.state === 'closed';
        if (this.isOpen() && !closing) {
            this.close();
        } else {
            this.open();
        }
    }

    isOpen() {
        return this.content?.classList.contains('rk-popover__content--active');
    }

    open() {
        if (!this.content) return;

        if (this.#cancelExitAnim) {
            this.#cancelExitAnim();
            this.#cancelExitAnim = null;
        }

        if (this.isOpen() && this.content.dataset.state !== 'closed') return;

        this.previousFocus = document.activeElement;
        this.content.classList.add('rk-popover__content--active');
        this.content.dataset.state = 'open';
        this.trigger?.setAttribute('aria-expanded', 'true');

        this.#reposition();
        this.#addGlobalListeners();

        requestAnimationFrame(() => {
            const firstFocusable = this.content.querySelector(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );
            if (firstFocusable) {
                firstFocusable.focus();
            }
        });

        this.rkEmit('popover:open', { id: this.content.id });
    }

    close() {
        if (!this.content || !this.isOpen()) return;
        if (this.content.dataset.state === 'closed') return;

        this.content.dataset.state = 'closed';
        this.#removeGlobalListeners();

        const finalize = () => {
            this.#cancelExitAnim = null;
            this.content.classList.remove('rk-popover__content--active');
            delete this.content.dataset.state;
            this.trigger?.setAttribute('aria-expanded', 'false');

            if (this.previousFocus) {
                this.previousFocus.focus();
                this.previousFocus = null;
            }

            this.rkEmit('popover:close', { id: this.content.id });
        };

        this.#cancelExitAnim = waitForExitAnimation(this.content, finalize);
    }

    #reposition() {
        if (!this.trigger || !this.content || !this.isOpen()) return;

        const preferredSide = this.dataset.side || 'bottom';
        const align = this.dataset.align || 'center';
        const sideOffset = parseInt(this.dataset.sideOffset, 10) || 8;
        const alignOffset = parseInt(this.dataset.alignOffset, 10) || 0;

        const triggerRect = this.trigger.getBoundingClientRect();
        const contentRect = this.content.getBoundingClientRect();
        const viewportW = window.innerWidth;
        const viewportH = window.innerHeight;

        const side = this.#resolveSide(preferredSide, triggerRect, contentRect, viewportW, viewportH, sideOffset);

        let top = 0;
        let left = 0;

        switch (side) {
            case 'top':
                top = -(contentRect.height + sideOffset);
                break;
            case 'bottom':
                top = triggerRect.height + sideOffset;
                break;
            case 'left':
                left = -(contentRect.width + sideOffset);
                break;
            case 'right':
                left = triggerRect.width + sideOffset;
                break;
        }

        if (side === 'top' || side === 'bottom') {
            switch (align) {
                case 'start':
                    left = alignOffset;
                    break;
                case 'center':
                    left = (triggerRect.width - contentRect.width) / 2 + alignOffset;
                    break;
                case 'end':
                    left = triggerRect.width - contentRect.width + alignOffset;
                    break;
            }
        } else {
            switch (align) {
                case 'start':
                    top = alignOffset;
                    break;
                case 'center':
                    top = (triggerRect.height - contentRect.height) / 2 + alignOffset;
                    break;
                case 'end':
                    top = triggerRect.height - contentRect.height + alignOffset;
                    break;
            }
        }

        const absLeft = triggerRect.left + left;
        const absTop = triggerRect.top + top;

        if (absLeft < 8) {
            left += (8 - absLeft);
        } else if (absLeft + contentRect.width > viewportW - 8) {
            left -= (absLeft + contentRect.width - viewportW + 8);
        }

        if (absTop < 8) {
            top += (8 - absTop);
        } else if (absTop + contentRect.height > viewportH - 8) {
            top -= (absTop + contentRect.height - viewportH + 8);
        }

        this.content.style.top = `${top}px`;
        this.content.style.left = `${left}px`;
        this.content.dataset.side = side;
    }

    #resolveSide(preferred, triggerRect, contentRect, vw, vh, offset) {
        const space = {
            top: triggerRect.top - offset,
            bottom: vh - triggerRect.bottom - offset,
            left: triggerRect.left - offset,
            right: vw - triggerRect.right - offset,
        };

        const fits = {
            top: space.top >= contentRect.height,
            bottom: space.bottom >= contentRect.height,
            left: space.left >= contentRect.width,
            right: space.right >= contentRect.width,
        };

        if (fits[preferred]) return preferred;

        const opposite = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };
        if (fits[opposite[preferred]]) return opposite[preferred];

        const sorted = Object.entries(space).sort((a, b) => b[1] - a[1]);
        return sorted[0][0];
    }

    #addGlobalListeners() {
        this.#onClickOutside = (e) => {
            if (!this.contains(e.target)) this.close();
        };
        this.#onKeyDown = (e) => {
            if (e.key === 'Escape' && this.isOpen()) {
                e.preventDefault();
                this.close();
            }
        };
        this.#onResize = this.#debounce(() => this.#reposition(), 100);

        document.addEventListener('click', this.#onClickOutside, true);
        document.addEventListener('keydown', this.#onKeyDown);
        window.addEventListener('resize', this.#onResize);
        window.addEventListener('scroll', this.#onResize, true);
    }

    #removeGlobalListeners() {
        if (this.#onClickOutside) {
            document.removeEventListener('click', this.#onClickOutside, true);
            this.#onClickOutside = null;
        }
        if (this.#onKeyDown) {
            document.removeEventListener('keydown', this.#onKeyDown);
            this.#onKeyDown = null;
        }
        if (this.#onResize) {
            window.removeEventListener('resize', this.#onResize);
            window.removeEventListener('scroll', this.#onResize, true);
            this.#onResize = null;
        }
    }

    #uid() {
        return Math.random().toString(36).substring(2, 9);
    }

    #debounce(fn, delay) {
        let timer;
        return (...args) => {
            clearTimeout(timer);
            timer = setTimeout(() => fn(...args), delay);
        };
    }
}

if (!customElements.get('rk-popover-element')) {
    customElements.define('rk-popover-element', RkPopoverElement);
}
